import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { ApiError } from '../../api/client'
import {
  aiAgentShellQuery,
  connectAiLabRow,
  saveAiAgent,
  type AiAuditTab,
  type AiBadges,
  type AiAuditRow,
  type AiScope,
} from '../../api/settingsAiAgent'
import { Forbidden } from '../../ui/Forbidden'
import { Spinner } from '../../ui/Spinner'
import { toast } from '../../ui/notify'
import { usePageStyles } from '../../ui/usePageStyles'
import { AgentModelsTab } from './AgentModelsTab'
import { AlarmModelTab } from './AlarmModelTab'
import { AuditPane, AuditTools, type AuditFilters } from './AuditSection'
import { BudgetsTab } from './BudgetsTab'
import { CompaniesTab } from './CompaniesTab'
import { CostReportTab } from './CostReportTab'
import { IncidentTab } from './IncidentTab'
import { LabsTab } from './LabsTab'
import { Badge, failed, staleLabIds, type GlobalDraft } from './parts'
import { PeopleTab } from './PeopleTab'
import { PlatformAdminsTab } from './PlatformAdminsTab'
import { RequestTypesTab } from './RequestTypesTab'
import { PlaybooksTab } from './PlaybooksTab'
import { RunbooksTab } from './RunbooksTab'

/**
 * Settings -> AI Agent. Perechea veche:
 * `application/controllers/settings/Ai_agent.php` si machetele din
 * `views/version-3/settings/ai_agent/`.
 *
 * DE CE ECRANUL APARE DE DOUA ORI IN RAIL
 * ---------------------------------------
 * Fiindca are doua audiente, si fiecare vine cu alta intrebare:
 *
 *   GLOBAL  `/app/settings/ai_agent`          al PLATFORMEI - laboratoarele,
 *           cheia, treptele, ce mostenesc toate firmele, toate jurnalele.
 *           Poarta: proprietarul (aceeasi lista de adrese ca Agent Releases).
 *   GENERAL `/app/settings/ai_agent/company`  al FIRMEI din sesiune - modelul
 *           ei, treapta, bugetul, oamenii si runbook-urile ei, si numai
 *           jurnalele ei. Poarta: si un Super Admin al firmei.
 *
 * Ce se vede la fiecare e hotarat pe SERVER (`AiAgentSettings::shell()`), nu
 * aici: un buton ascuns nu e o poarta.
 *
 * SAVE LA GLOBAL ADUNA TOT ce e global din pagina - comutatorul din Agent
 * Models, cheia si modelul de rezerva din AI Labs & Models, implicitele din
 * Companies - oricare din cele trei butoane ar fi apasat, fiindca serverul
 * rescrie randul intreg si un salvat pe jumatate ar sterge cealalta jumatate.
 * De aia ciorna sta AICI, in pagina, si nu in fiecare fila.
 */

const STYLES = ['css/ai_audit.css']

type TabId =
  | 'models'
  | 'setup'
  | 'companies'
  | 'people'
  | 'alarms'
  | 'budgets'
  | 'cost'
  | 'runbooks'
  | 'request_types'
  | 'playbooks'
  | 'catalog'
  | AiAuditTab

const LOGS: TabId[] = ['sessions', 'commands', 'changes', 'memory']

/** Adresele vechi cu `#aiaSessions` duc chiar la fila aia. */
const FROM_HASH: Record<string, TabId> = {
  aiamodels: 'models',
  aiasetup: 'setup',
  aiacompanies: 'companies',
  /* "People" si "Platform admins & Adam access" sunt o fila din 29 octombrie
     2026, "People & access": linkurile vechi la oricare duc acolo. */
  aiapeople: 'people',
  aiaadmins: 'people',
  aiapeopleaccess: 'people',
  aiaalarms: 'alarms',
  aiabudgets: 'budgets',
  aiacost: 'cost',
  aiarunbooks: 'runbooks',
  aiarequesttypes: 'request_types',
  aiaplaybooks: 'playbooks',
  aiacatalog: 'catalog',
  aiasessions: 'sessions',
  aiacommands: 'commands',
  aiachanges: 'changes',
  aiamemory: 'memory',
}

export function AiAgentSettingsPage({ scope }: { scope: AiScope }) {
  const client = useQueryClient()
  const shell = useQuery(aiAgentShellQuery(scope))
  const page = shell.data ?? null

  usePageStyles(STYLES, page?.version ?? '')

  const [tab, setTab] = useState<TabId | null>(null)
  const [draft, setDraft] = useState<GlobalDraft | null>(null)
  const [badges, setBadges] = useState<AiBadges | null>(null)
  const [rows, setRows] = useState<AiAuditRow[]>([])
  const [filters, setFilters] = useState<AuditFilters>({
    search: '',
    days: 30,
    companyId: 0,
    failedOnly: false,
  })

  /* La Global drumul incepe cu laboratoarele: fara un laborator cu cheie nu e
     nimic de ales in trepte. La General, firma n-are laboratoare - incepe cu
     modelele ei. */
  useEffect(() => {
    if (!page || tab !== null) return

    const wanted = FROM_HASH[window.location.hash.replace('#', '').toLowerCase()]
    setTab(wanted ?? (page.scope === 'company' ? 'setup' : 'models'))
  }, [page, tab])

  useEffect(() => {
    if (!page || draft !== null) return

    setDraft({
      enabled: page.ai.enabled,
      effort: page.ai.effort,
      maxSteps: page.ai.max_steps,
      dailyBudget: page.ai.daily_token_budget,
      fallbackLab: page.ai.lab,
      fallbackBaseUrl: page.ai.base_url,
      fallbackModel: page.ai.model,
      apiKey: '',
      brainUrl: page.ai.brain_url,
      brainToken: '',
    })
  }, [page, draft])

  useEffect(() => {
    if (page && badges === null) setBadges(page.badges)
    if (page) setFilters((f) => (f.companyId === 0 && page.scope === 'company' ? { ...f, companyId: page.companyId } : f))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page])

  /* Listele de modele mai vechi de o zi se reiau singure, o data pe deschidere
     de ecran, numai la Global si numai pentru proprietar (Connect e al lui).
     Un model nou lansat intre timp apare asa in dropdown-ul treptelor fara sa
     apese nimeni Connect. Un Connect picat ramane pe randul laboratorului, ca
     si cel apasat de mana; lista veche nu se sterge. */
  const [labsRefreshed, setLabsRefreshed] = useState(false)
  useEffect(() => {
    if (!page || labsRefreshed) return
    setLabsRefreshed(true)
    if (page.scope === 'company' || !page.isOwner) return

    const stale = staleLabIds(page.aiLabs ?? [], Date.now())
    if (stale.length === 0) return

    void Promise.all(stale.map((id) => connectAiLabRow(id).then((r) => r.ok === true).catch(() => false))).then(
      (answers) => {
        if (answers.some(Boolean)) void client.invalidateQueries({ queryKey: ['settings', 'ai_agent', scope] })
      }
    )
  }, [page, labsRefreshed, client, scope])

  if (shell.error instanceof ApiError && shell.error.status === 403) return <Forbidden />
  if (!page || !draft || tab === null) return <Spinner />

  const reload = () => {
    void client.invalidateQueries({ queryKey: ['settings', 'ai_agent', scope] })
  }

  const saveGlobal = () => {
    void saveAiAgent({
      enabled: draft.enabled ? 1 : 0,
      lab: draft.fallbackLab,
      base_url: draft.fallbackBaseUrl,
      model: draft.fallbackModel,
      api_key: draft.apiKey,
      brain_url: draft.brainUrl,
      brain_token: draft.brainToken,
      daily_token_budget: draft.dailyBudget,
      effort: draft.effort,
      max_steps: draft.maxSteps,
    })
      .then((r) => {
        if (!r.ok) {
          toast(r.error ?? 'Could not save.', 'AI Agent', 'error')
          return
        }
        toast('Saved.', 'AI Agent')
        /* Cheia scrisa nu se mai tine in pagina: in baza sta cifrata, si casuta
           goala inseamna de-acum "nu o schimba". */
        setDraft({ ...draft, apiKey: '', brainToken: '' })
        reload()
      })
      .catch((error: unknown) => toast(failed(error), 'AI Agent', 'error'))
  }

  const isCompany = page.scope === 'company'
  const isLog = LOGS.includes(tab)

  return (
    <>
      <nav className="od-tabbar">
        <ul className="od-tabbar-nav">
          <li className="menu-header">AI AGENT</li>
          {!isCompany ? (
            <Tab id="models" tab={tab} onPick={setTab} icon="fas fa-flask" label="AI Labs & Models">
              <Badge value={page.aiLabs.length} count />
            </Tab>
          ) : null}
          <Tab id="setup" tab={tab} onPick={setTab} icon="fas fa-sliders-h" label="Agent Models" />
          {!isCompany ? (
            <Tab id="companies" tab={tab} onPick={setTab} icon="fas fa-building" label="Companies">
              <Badge value={page.companies?.rows.length ?? 0} count />
            </Tab>
          ) : null}
          {/* Cine foloseste Adam si cum. Pe Global, administratorul de platforma
              vede aici si lista adminilor si treptele lui Adam (poarta adevarata
              e pe server); ceilalti vad doar lista pe firme, ca in fila People. */}
          <Tab id="people" tab={tab} onPick={setTab} icon="fas fa-users" label="People & access">
            <Badge value={page.people.length} count />
          </Tab>
          {/* Modelul diagnosticului automat al alarmelor: tot numai pentru un
              administrator de platforma - el hotaraste factura. */}
          {!isCompany && page.isOwner ? (
            <Tab id="alarms" tab={tab} onPick={setTab} icon="fas fa-bell" label="Alarm model" />
          ) : null}
          {/* Bugetul lui Adam in bani: tot numai administratorul platformei. */}
          {!isCompany && page.isOwner ? (
            <Tab id="budgets" tab={tab} onPick={setTab} icon="fas fa-wallet" label="Budgets" />
          ) : null}
          {/* Raportul de cost: platforma vede toate firmele, adminul firmei numai
              firma lui (poarta e pe server). */}
          <Tab id="cost" tab={tab} onPick={setTab} icon="fas fa-receipt" label="Cost report" />
          <Tab id="runbooks" tab={tab} onPick={setTab} icon="fas fa-book-medical" label="Runbooks">
            <Badge value={page.runbooks.length} count />
          </Tab>
          {isCompany || page.isOwner ? (
            <Tab id="request_types" tab={tab} onPick={setTab} icon="fas fa-tags" label="Request types" />
          ) : null}
          <Tab id="playbooks" tab={tab} onPick={setTab} icon="fas fa-lightbulb" label="Playbooks" />
          {!isCompany ? (
            <Tab id="catalog" tab={tab} onPick={setTab} icon="fas fa-first-aid" label="Incident catalog">
              <Badge value={page.catalog === null ? null : page.catalog.length} count />
            </Tab>
          ) : null}
          <Tab id="sessions" tab={tab} onPick={setTab} icon="fas fa-comments" label="Sessions">
            <Badge value={(badges ?? page.badges).sessions} />
          </Tab>
          <Tab id="commands" tab={tab} onPick={setTab} icon="fas fa-terminal" label="Commands">
            <Badge value={(badges ?? page.badges).commands} />
          </Tab>
          <Tab id="changes" tab={tab} onPick={setTab} icon="fas fa-code-branch" label="Change Requests">
            <Badge value={(badges ?? page.badges).changes} />
          </Tab>
          {/* Ce tine minte agentul nu e audit - e ce STIE despre parc - dar se
              citeste la fel, cu aceeasi bara de filtre deasupra. */}
          <Tab id="memory" tab={tab} onPick={setTab} icon="fas fa-brain" label="Memory" />
          <li className="menu-block-divider"></li>
        </ul>
      </nav>

      <div className="page-content-wrapper">
        <div id="content">
          <h3 style={{ marginTop: 0 }}>{isCompany ? `AI Agent · ${page.companyName}` : 'AI Agent · Global'}</h3>
          <p className="text-muted" style={{ fontSize: 13 }}>
            {page.pageDescription}
          </p>

          {!page.installed ? (
            /* Cauza reala a lui "tot pare sa nu mearga": tabelele lipsesc. */
            <div className="panel panel-default" style={{ borderLeft: '4px solid #b42318' }}>
              <div className="panel-body" style={{ background: '#fdecea' }}>
                <strong style={{ color: '#b42318' }}>The migrations have not run yet.</strong> The AI agent needs its
                tables in the database - until the migrations run, this screen cannot save anything (not the key, not
                the model), however many times you press Save.
                <div style={{ marginTop: 8, fontSize: 13 }}>
                  See them at <a href="/app/settings/change_log">Change Log → Database migrations</a>, and run them on a
                  machine that can reach the database:
                </div>
                <pre
                  style={{
                    margin: '6px 0 0',
                    padding: '8px 10px',
                    background: '#fff',
                    borderRadius: 6,
                    fontSize: 11,
                    whiteSpace: 'pre-wrap',
                  }}
                >
                  {`cd migrations.optimumdesk.com
php artisan migrate --env=cec --pretend   # shows what would run
php artisan migrate --env=cec             # runs it`}
                </pre>
              </div>
            </div>
          ) : null}

          {isLog ? (
            <AuditTools page={page} filters={filters} onFilters={setFilters} tab={tab as AiAuditTab} rows={rows} />
          ) : null}

          {/*
            FARA `tab-content`. In macheta veche clasa venea cu `.tab-pane`-urile
            Bootstrap si statea lipita sub `nav-tabs`, alba si ea - deci fondul
            alb din app.css (`.tab-content { background: #FFF; padding: 15px 0;
            margin-bottom: 20px }`) se citea drept corpul filelor. Aici filele
            sunt in `od-tabbar`, iar intre ele si corp sta titlul pe fondul gri:
            acelasi fond alb iesea ca un al doilea card, pornit de sub subtitlu
            si lung cu 27px sub chenarul ultimului panou (masurat in Edge, in
            ordinea foilor din productie). Filele de aici nu sunt `.tab-pane`,
            deci clasa nu mai aducea nimic altceva.
          */}
          {/* 5px de aer sub subtitlu: fara ele primul card se lipea de el (Roxana, 15 septembrie 2026). */}
          <div className="aia-outer" style={{ marginTop: 5 }}>
            {tab === 'setup' ? (
              <AgentModelsTab
                page={page}
                draft={draft}
                onDraft={setDraft}
                onSaveGlobal={saveGlobal}
                onReload={reload}
              />
            ) : null}
            {tab === 'models' ? (
              <LabsTab page={page} draft={draft} onDraft={setDraft} onSaveGlobal={saveGlobal} onReload={reload} />
            ) : null}
            {tab === 'companies' ? (
              <CompaniesTab page={page} draft={draft} onDraft={setDraft} onSaveGlobal={saveGlobal} />
            ) : null}
            {tab === 'people' ? (
              !isCompany && page.isOwner ? (
                <PlatformAdminsTab shell={page} onShellChanged={reload} />
              ) : (
                <PeopleTab page={page} onReload={reload} />
              )
            ) : null}
            {tab === 'alarms' && !isCompany && page.isOwner ? <AlarmModelTab /> : null}
            {tab === 'budgets' && !isCompany && page.isOwner ? <BudgetsTab onPeople={() => setTab('people')} /> : null}
            {tab === 'cost' ? <CostReportTab /> : null}
            {tab === 'runbooks' ? <RunbooksTab page={page} onReload={reload} /> : null}
            {tab === 'request_types' && (isCompany || page.isOwner) ? <RequestTypesTab scope={isCompany ? 'company' : 'global'} /> : null}
            {tab === 'playbooks' ? <PlaybooksTab scope={page.scope} /> : null}
            {tab === 'catalog' ? <IncidentTab page={page} /> : null}
            {isLog ? (
              <AuditPane
                page={page}
                tab={tab as AiAuditTab}
                filters={filters}
                onRows={setRows}
                onBadges={setBadges}
              />
            ) : null}
          </div>
        </div>
      </div>
    </>
  )
}

function Tab({
  id,
  tab,
  onPick,
  icon,
  label,
  children,
}: {
  id: TabId
  tab: TabId
  onPick(next: TabId): void
  icon: string
  label: string
  children?: React.ReactNode
}) {
  return (
    <li className={tab === id ? 'menu-tab active' : 'menu-tab'}>
      <a
        href={'#' + id}
        onClick={(e) => {
          e.preventDefault()
          onPick(id)
        }}
      >
        <i className={icon} aria-hidden="true"></i> {label} {children}
      </a>
    </li>
  )
}

/** Ruta `/app/settings/ai_agent` - zona Global. */
export function AiAgentGlobalPage() {
  return <AiAgentSettingsPage scope="global" />
}

/** Ruta `/app/settings/ai_agent/company` - zona General. */
export function AiAgentCompanyPage() {
  return <AiAgentSettingsPage scope="company" />
}
